import type { Vec2 } from '../shared/types';

/**
 * Minimal DXF HATCH reader. `dxf-parser` ignores HATCH entities entirely, so we
 * scan the raw DXF text for them and extract their boundary loops plus colour.
 *
 * Supported boundary paths:
 *  - polyline boundaries (the common case for area fills);
 *  - edge boundaries made of line and arc edges (arcs are sampled);
 *  - ellipse/spline edges are approximated by their explicit vertices.
 *
 * Hatch *patterns* (the actual hatch line geometry) are not reproduced: every
 * hatch is imported as a single filled region, which is the right read for plan
 * drawings. `solid` records whether the source hatch was a solid fill.
 */
export interface RawHatch {
  loops: Vec2[][];
  solid: boolean;
  colorIndex?: number;
  trueColor?: number;
  layer?: string;
}

interface Pair {
  code: number;
  value: string;
}

function tokenize(text: string): Pair[] {
  const raw = text.split(/\r\n|\r|\n/);
  const pairs: Pair[] = [];
  let i = 0;
  while (i + 1 < raw.length) {
    const code = parseInt(raw[i].trim(), 10);
    if (Number.isNaN(code)) {
      i += 1; // resync on stray lines
      continue;
    }
    pairs.push({ code, value: raw[i + 1] });
    i += 2;
  }
  return pairs;
}

/** Extract every HATCH entity from a DXF document. */
export function parseHatches(dxfText: string): RawHatch[] {
  if (!/\bHATCH\b/.test(dxfText)) return [];
  const pairs = tokenize(dxfText);

  // Restrict to the ENTITIES section so we don't pick up hatches that live
  // inside BLOCK definitions (those are placed via INSERT and we don't expand
  // them here). Fall back to the whole file if there's no explicit section.
  let secStart = 0;
  let secEnd = pairs.length;
  for (let i = 0; i < pairs.length; i++) {
    if (pairs[i].code === 2 && pairs[i].value.trim() === 'ENTITIES') {
      secStart = i + 1;
      for (let j = secStart; j < pairs.length; j++) {
        if (pairs[j].code === 0 && pairs[j].value.trim() === 'ENDSEC') {
          secEnd = j;
          break;
        }
      }
      break;
    }
  }

  // Find entity boundaries (code 0). For each HATCH, parse its pair range.
  const starts: number[] = [];
  for (let i = secStart; i < secEnd; i++) if (pairs[i].code === 0) starts.push(i);

  const hatches: RawHatch[] = [];
  for (let s = 0; s < starts.length; s++) {
    const begin = starts[s];
    if (pairs[begin].value.trim() !== 'HATCH') continue;
    const end = s + 1 < starts.length ? starts[s + 1] : secEnd;
    const h = parseHatch(pairs, begin + 1, end);
    if (h && h.loops.length) hatches.push(h);
  }
  return hatches;
}

function parseHatch(pairs: Pair[], begin: number, end: number): RawHatch | null {
  let solid = false;
  let colorIndex: number | undefined;
  let trueColor: number | undefined;
  let layer: string | undefined;
  let numPaths = 0;
  let pathsStart = -1;

  for (let i = begin; i < end; i++) {
    const { code, value } = pairs[i];
    if (code === 8) layer = value.trim();
    else if (code === 62) colorIndex = parseInt(value.trim(), 10);
    else if (code === 420) trueColor = parseInt(value.trim(), 10);
    else if (code === 70) solid = parseInt(value.trim(), 10) === 1;
    else if (code === 91) {
      numPaths = parseInt(value.trim(), 10) || 0;
      pathsStart = i + 1;
      break;
    }
  }
  if (pathsStart < 0 || numPaths <= 0) return null;

  const loops = parseBoundaryPaths(pairs, pathsStart, end, numPaths);
  return { loops, solid, colorIndex, trueColor, layer };
}

function parseBoundaryPaths(pairs: Pair[], start: number, end: number, numPaths: number): Vec2[][] {
  const loops: Vec2[][] = [];
  let cursor = start;
  for (let p = 0; p < numPaths && cursor < end; p++) {
    // Each boundary path begins with code 92 (path type flag).
    while (cursor < end && pairs[cursor].code !== 92) cursor++;
    if (cursor >= end) break;
    const flag = parseInt(pairs[cursor].value.trim(), 10) || 0;
    const isPolyline = (flag & 2) !== 0;
    // The boundary data runs until code 97 (number of source objects).
    let region = cursor + 1;
    let regionEnd = region;
    while (regionEnd < end && pairs[regionEnd].code !== 97 && pairs[regionEnd].code !== 92) regionEnd++;

    const loop = isPolyline
      ? readPolylineLoop(pairs, region, regionEnd)
      : readEdgeLoop(pairs, region, regionEnd);
    if (loop.length >= 2) loops.push(loop);
    cursor = regionEnd;
  }
  return loops;
}

/** A polyline boundary: collect every (10,20) vertex in order. */
function readPolylineLoop(pairs: Pair[], start: number, end: number): Vec2[] {
  const pts: Vec2[] = [];
  for (let i = start; i < end; i++) {
    if (pairs[i].code === 10) {
      const x = parseFloat(pairs[i].value);
      let y = 0;
      if (i + 1 < end && pairs[i + 1].code === 20) y = parseFloat(pairs[i + 1].value);
      pts.push({ x, y });
    }
  }
  return dedupe(pts);
}

/** An edge boundary: split the region by code 72 (edge type) and join edges. */
function readEdgeLoop(pairs: Pair[], start: number, end: number): Vec2[] {
  // Find each edge's starting index (a code 72 introduces a new edge).
  const edgeStarts: number[] = [];
  for (let i = start; i < end; i++) if (pairs[i].code === 72) edgeStarts.push(i);
  if (!edgeStarts.length) {
    // Fall back to any explicit vertices in the region.
    return readPolylineLoop(pairs, start, end);
  }
  const pts: Vec2[] = [];
  for (let e = 0; e < edgeStarts.length; e++) {
    const es = edgeStarts[e];
    const ee = e + 1 < edgeStarts.length ? edgeStarts[e + 1] : end;
    const type = parseInt(pairs[es].value.trim(), 10) || 0;
    const get = (code: number): number | undefined => {
      for (let i = es; i < ee; i++) if (pairs[i].code === code) return parseFloat(pairs[i].value);
      return undefined;
    };
    if (type === 2) {
      // Arc edge: centre (10,20), radius 40, start 50, end 51, ccw 73.
      const cx = get(10) ?? 0;
      const cy = get(20) ?? 0;
      const r = get(40) ?? 0;
      const a0 = get(50) ?? 0;
      const a1 = get(51) ?? 360;
      const ccw = (get(73) ?? 1) !== 0;
      for (const pt of sampleArc(cx, cy, r, a0, a1, ccw)) pts.push(pt);
    } else {
      // Line (1) or unknown: use start (10,20) and end (11,21) if present.
      const sx = get(10);
      const sy = get(20);
      if (sx !== undefined && sy !== undefined) pts.push({ x: sx, y: sy });
      const ex = get(11);
      const ey = get(21);
      if (ex !== undefined && ey !== undefined) pts.push({ x: ex, y: ey });
    }
  }
  return dedupe(pts);
}

function sampleArc(cx: number, cy: number, r: number, a0Deg: number, a1Deg: number, ccw: boolean): Vec2[] {
  let sweep = a1Deg - a0Deg;
  if (ccw) {
    while (sweep <= 0) sweep += 360;
  } else {
    while (sweep >= 0) sweep -= 360;
  }
  const segs = Math.max(4, Math.ceil(Math.abs(sweep) / 15));
  const pts: Vec2[] = [];
  for (let i = 0; i <= segs; i++) {
    const a = ((a0Deg + (sweep * i) / segs) * Math.PI) / 180;
    pts.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
  }
  return pts;
}

function dedupe(pts: Vec2[]): Vec2[] {
  const out: Vec2[] = [];
  for (const p of pts) {
    const last = out[out.length - 1];
    if (!last || Math.abs(last.x - p.x) > 1e-6 || Math.abs(last.y - p.y) > 1e-6) out.push(p);
  }
  // Drop a duplicated closing vertex.
  if (out.length > 1) {
    const a = out[0];
    const b = out[out.length - 1];
    if (Math.abs(a.x - b.x) < 1e-6 && Math.abs(a.y - b.y) < 1e-6) out.pop();
  }
  return out;
}
